import React, { useContext, useEffect, useState, useCallback, useMemo } from 'react';
import { IAppContext, AppContext } from 'appState/appContext';
import { TEditUserSelectedItem } from '../../../../../../editUserState/editUserContext';
import { IItemForGetShortInfo, UserRoles, IGetRestaurantsShort, IGetRestaurantsShortArgs } from 'types';
import { getRestaurantsShortInfo } from 'backend/restaurants';
import { useRoleAccess } from 'hooks/useRoleAccess';
import { useFetchData } from 'hooks/useFetchData';
import MultipleSelect from './components/MultipleSelect';
import SelectedItem from 'components/SelectedItem/SelectedItem';
import style from './RestaurantsMultipleSelect.module.scss';

interface IRestaurantsMultipleSelect {
	setRestaurants: (value: string[]) => void;
	setFetchedRestaurants: (value: IItemForGetShortInfo[]) => void;
	selectedCustomer: TEditUserSelectedItem;
	selectedRestaurants: string[];
	initialIDsSelectedRestaurant: string[];
}

const RestaurantsMultipleSelect: React.FC<IRestaurantsMultipleSelect> = ({
	setRestaurants,
	setFetchedRestaurants,
	selectedCustomer,
	selectedRestaurants,
	initialIDsSelectedRestaurant,
}) => {
	const { loggedUser } = useContext<IAppContext>(AppContext);
	const hasAccess = useRoleAccess([
		UserRoles.SUPER_USER,
		UserRoles.CLIENT_MANAGER,
		UserRoles.GROUP_MANAGER,
		UserRoles.TECHNICAL,
	]);
	const { response, loading, refetch, responseError } = useFetchData<IGetRestaurantsShort, IGetRestaurantsShortArgs>(
		selectedCustomer ? getRestaurantsShortInfo : null,
		{
			customerId: selectedCustomer || '',
		},
	);
	const [isFirstRender, setIsFirstRender] = useState<boolean>(true);

	const handleRemove = useCallback(
		(id: string) => {
			const updatedSelectedItems = selectedRestaurants.filter((el) => el !== id);
			setRestaurants(updatedSelectedItems);
		},
		[selectedRestaurants, setRestaurants],
	);

	const restaurantsList = useMemo(() => {
		return selectedRestaurants.map((el) => {
			const label = response?.data.restaurants.find((item) => item.id === el)?.name || '';
			const accessibleRestaurantIds = loggedUser.restaurants?.map((el) => el.id);
			return (
				<SelectedItem
					key={el}
					label={label}
					onDelete={!hasAccess && !accessibleRestaurantIds?.includes(el) ? undefined : () => handleRemove(el)}
				/>
			);
		});
	}, [selectedRestaurants, response, loggedUser, hasAccess, handleRemove]);

	useEffect(() => {
		refetch();
	}, [selectedCustomer]);

	useEffect(() => {
		if (response) {
			const accessibleRestaurants = loggedUser.restaurants ? loggedUser.restaurants : [];
			const restaurants = hasAccess ? response.data.restaurants : accessibleRestaurants;
			setFetchedRestaurants(restaurants);
		}
		if (isFirstRender && response) {
			setRestaurants(initialIDsSelectedRestaurant);
			setIsFirstRender(false);
		}
	}, [response, loggedUser, hasAccess]);

	return (
		<div className={style.container} data-test="RestaurantsField">
			<div className={style.field}>
				<MultipleSelect
					data={response?.data.restaurants || []}
					isLoading={loading || isFirstRender}
					refetch={refetch}
					responseError={responseError}
					setIDsRestaurant={setRestaurants}
					IDSelectedCustomer={selectedCustomer}
					IDsSelectedRestaurant={selectedRestaurants}
				/>
			</div>
			<div className={style.groupsList}>{restaurantsList}</div>
		</div>
	);
};

export default React.memo(RestaurantsMultipleSelect);
